import { t } from "@travily/i18n";
import { Logo } from "@ui/components/Logo";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

const topBarWeb = tv({
  slots: {
    root: "flex flex-col bg-white",
    inner: "grid h-[88px] w-full grid-cols-[1fr_auto_1fr] items-center px-8",
    logoFull: "hidden h-[42px] lg:inline-flex",
    logoMark: "h-[42px] lg:hidden",
    center: "col-start-2 flex items-center justify-center gap-4",
    end: "col-start-3 flex items-center justify-end gap-3",
  },
  variants: {
    constrained: {
      // Gutter moves onto the root so the inner grid's content aligns with a
      // max-w-container page body that uses the same px-4 xl:px-8 gutter.
      true: { root: "px-4 xl:px-8", inner: "mx-auto max-w-container px-0" },
    },
  },
});

export interface TopBarWebProps {
  bottomContent?: ReactNode;
  centerContent?: ReactNode;
  className?: string;
  /** Cap the inner grid at the content container width (auth pages align the bar with the card below). */
  constrained?: boolean;
  endContent?: ReactNode;
}

export function TopBarWeb({ bottomContent, centerContent, className, constrained, endContent }: TopBarWebProps) {
  const styles = topBarWeb({ constrained });

  return (
    <header className={styles.root({ className })}>
      <div className={styles.inner()}>
        <Link aria-label={t("components.top_bar.logo")} cacheFor={NAV_PREFETCH_CACHE_FOR} href="/" prefetch="hover">
          <Logo className={styles.logoFull()} variant="full" />
          <Logo className={styles.logoMark()} variant="mark" />
        </Link>
        {centerContent && <nav className={styles.center()}>{centerContent}</nav>}
        <div className={styles.end()}>{endContent}</div>
      </div>
      {bottomContent}
    </header>
  );
}
